import {
  useReducer, useRef, useCallback, useEffect, useState,
} from 'react';
import { useSelector } from 'react-redux';
import { registerRewardedSlot } from 'src/services/adManager';
import {
  requestRewardedAdNonce, confirmRewardedAd, getRewardedAdStatus,
} from 'src/modules/common/api';
import { getCaptchaToken } from 'src/utils/captcha';
import {
  getPostHog,
  trackRewardedAdCtaClicked,
  trackRewardedAdNonceRequested,
  trackRewardedAdLoaded,
  trackRewardedAdLoadFailed,
  trackRewardedAdCompleted,
  trackRewardedAdDismissed,
  trackRewardedAdSsvConfirmed,
  trackRewardedAdSsvTimeout,
  trackRewardedAdCapReached,
  trackRewardedAdBlocked,
  trackRewardedAdExperiment,
  captureException,
} from 'src/services/analytics';

/**
 * State machine for a single rewarded-video ad watch.
 *
 *   idle
 *     → startAd() → requesting-nonce
 *
 *   requesting-nonce  (5s timeout via AbortController)
 *     → 200 → loading-ad
 *     → 403 cap → cap-reached
 *     → other / abort / timeout → failed
 *
 *   loading-ad   (8s timeout, slot defined via registerRewardedSlot)
 *     → rewardedSlotReady (auto-shows the ad) → playing
 *     → no_fill / timeout → no-fill
 *
 *   playing
 *     → rewardedSlotGranted → confirming
 *     → rewardedSlotClosed without grant → idle (track _dismissed)
 *
 *   confirming  (POST /users/me/rewarded-ad/confirm — synchronous credit)
 *     → 200 → completed (dispatch USER_CONSTANTS.UPDATE { timeToFreeVote: null })
 *     → 410 nonce_invalid → failed
 *     → 403 cap → cap-reached
 *     → network error / 5s timeout → failed (ssv_timeout — preserves the
 *       original event name even though SSV is no longer the credit path,
 *       so the dashboard funnel stays stable)
 *
 * A/B bucketing: PostHog feature flag `rewarded-video-skip` decides
 * treatment vs control. The hook short-circuits to "disabled" for the
 * control bucket, ensuring measurement integrity for the cannibalization
 * read.
 */

export const REWARDED_AD_STATE = Object.freeze({
  IDLE: 'idle',
  REQUESTING_NONCE: 'requesting-nonce',
  LOADING_AD: 'loading-ad',
  PLAYING: 'playing',
  CONFIRMING: 'confirming',
  COMPLETED: 'completed',
  FAILED: 'failed',
  NO_FILL: 'no-fill',
  CAP_REACHED: 'cap-reached',
  BLOCKED: 'blocked',
});

const NONCE_TIMEOUT_MS = 5000;
const AD_LOAD_TIMEOUT_MS = 8000;
const CONFIRM_TIMEOUT_MS = 5000;
// Backend returns HTTP 425 ("Too Early") when the SSV credit pool is empty
// because Google's SSV callback hasn't arrived yet. Google usually fires
// within 0.5-3s, so a few short retries cover the common case.
const CONFIRM_MAX_RETRIES = 3;
const CONFIRM_RETRY_DELAY_MS = 1500;
// Fallback only used until the first /status or /confirm response lands;
// the real cap comes from the server.
export const REWARDED_AD_CAP_DEFAULT = 15;

function reducer(state, action) {
  switch (action.type) {
    case 'set':
      if (state.status === action.status) return state;
      return { ...state, status: action.status };
    case 'cap': {
      const nextCap = action.cap !== undefined && action.cap !== null
        ? action.cap : state.dailyCap;
      if (
        state.dailyRemaining === action.remaining
        && state.dailyUsed === action.used
        && state.dailyCap === nextCap
      ) return state;
      return {
        ...state,
        dailyRemaining: action.remaining,
        dailyUsed: action.used,
        dailyCap: nextCap,
      };
    }
    default:
      return state;
  }
}

/**
 * @param {object} opts
 * @param {function} [opts.onCooldownCleared] - silent: called when /confirm credits the cooldown
 *   (clear Redux/localStorage). Fires while the ad may still be on screen, so it must NOT
 *   show any visible feedback.
 * @param {function} [opts.onSuccessVisible] - visible feedback (snackbar). Fires only once the
 *   reward is credited AND the ad iframe has closed, so the user actually sees it.
 */
export function useRewardedAd({ onCooldownCleared, onSuccessVisible } = {}) {
  const apiToken = useSelector((s) => s.user.apiToken);
  const userAdblock = useSelector((s) => s.app.userAdblock);
  const timeToFreeVote = useSelector((s) => s.user.timeToFreeVote);
  const featureFlag = useSelector((s) => s.user.features?.rewarded_video === true);
  const compileFlag = process.env.REACT_APP_REWARDED_VIDEO_ENABLED === 'true';
  const isProduction = process.env.REACT_APP_PRODUCTION === 'TRUE';

  // PostHog A/B bucket — drives cannibalization measurement.
  //
  //   Production: variant must be confirmed by PostHog before we render
  //   anything. Default-to-treatment would contaminate the holdout for
  //   returning users, whose first render happens before App's
  //   componentDidMount runs initAnalytics().
  //
  //   Dev/local: PostHog isn't initialized at all (initAnalytics short-
  //   circuits when REACT_APP_PRODUCTION !== 'TRUE'), so default to
  //   treatment so engineers see the CTA without faking flags.
  //
  // We poll briefly because PostHog's `getFeatureFlag` can return undefined
  // until the flags payload arrives over the network. `onFeatureFlags`
  // would be cleaner but isn't reliably exposed across posthog-js versions.
  const [variant, setVariant] = useState(() => (isProduction ? null : 'treatment'));

  useEffect(() => {
    if (!compileFlag || !featureFlag) return undefined;
    if (variant !== null) return undefined; // already resolved

    let cancelled = false;
    const tryRead = () => {
      if (cancelled) return false;
      const ph = getPostHog();
      if (!ph || typeof ph.getFeatureFlag !== 'function') return false;
      const v = ph.getFeatureFlag('rewarded-video-skip', { send_event: false });
      if (v === 'control' || v === 'treatment') {
        setVariant(v);
        return true;
      }
      return false;
    };

    if (tryRead()) return () => { cancelled = true; };

    // Subscribe if PostHog supports it (v1.30+).
    const ph = getPostHog();
    if (ph && typeof ph.onFeatureFlags === 'function') {
      const off = ph.onFeatureFlags(() => tryRead());
      return () => { cancelled = true; if (typeof off === 'function') off(); };
    }

    // Otherwise poll for ~5s while PostHog initializes / fetches flags.
    const interval = setInterval(() => { if (tryRead()) clearInterval(interval); }, 500);
    const stop = setTimeout(() => clearInterval(interval), 5000);
    return () => {
      cancelled = true;
      clearInterval(interval);
      clearTimeout(stop);
    };
  }, [compileFlag, featureFlag, variant]);

  const enabled = compileFlag && featureFlag && variant === 'treatment';

  // Emit the experiment-started event exactly once per session, only after
  // the variant has been resolved (so we don't pollute the funnel with a
  // bogus default).
  const expTrackedRef = useRef(false);
  useEffect(() => {
    if (compileFlag && featureFlag && variant && !expTrackedRef.current) {
      expTrackedRef.current = true;
      trackRewardedAdExperiment(variant);
    }
  }, [compileFlag, featureFlag, variant]);

  const [state, dispatch] = useReducer(reducer, {
    status: REWARDED_AD_STATE.IDLE,
    dailyRemaining: null,
    dailyUsed: 0,
    dailyCap: REWARDED_AD_CAP_DEFAULT,
  });

  // After a successful ad-skip the user casts their free vote, which
  // re-arms the cooldown (timeToFreeVote becomes positive again). At that
  // point the COMPLETED state has done its job; reset to IDLE so a second
  // ad in the same session is allowed. Also handles the in-popup case
  // where the user dismisses without voting and returns later — we don't
  // strand the machine in COMPLETED forever.
  useEffect(() => {
    if (state.status === REWARDED_AD_STATE.COMPLETED && timeToFreeVote && timeToFreeVote > 0) {
      dispatch({ type: 'set', status: REWARDED_AD_STATE.IDLE });
    }
  }, [state.status, timeToFreeVote]);

  const slotRef = useRef(null);
  const adLoadTimeoutRef = useRef(null);
  const nonceCtrlRef = useRef(null);
  const confirmCtrlRef = useRef(null);
  // Tracks the outcome of the last ad slot registration. Used by the
  // post-vote invitation modal to decide whether to risk showing a "watch
  // a quick ad" CTA — if the most recent attempt was a no_fill, suppress
  // the invitation for FILL_SUPPRESS_MS so we don't promise an ad and then
  // immediately tell the user "no video available."
  //
  // status: 'unknown' | 'fill' | 'no_fill'
  //   - 'unknown' is treated as optimistic — first-time users see the
  //     invitation; if it turns out to be no_fill, future invitations
  //     within 60s are suppressed.
  //   - 'fill' means rewardedSlotReady fired → an ad was successfully
  //     loaded → invitation is safe to show.
  //   - 'no_fill' means slotRenderEnded.isEmpty=true or load timeout → we
  //     suppress invitations for 60s, then re-enter optimistic mode.
  const lastFillRef = useRef({ status: 'unknown', at: 0 });
  // Monotonic session id. Bumped at the top of every startAd. Each async
  // callback (captcha resolution, fetch response, GPT slot event, retry
  // loop tick) captures the id at issue time and re-checks against the
  // current value before dispatching state. Stale callbacks from a torn-
  // down session bail without corrupting the new session's UI.
  //
  // Why an int counter instead of the previous boolean cancelledRef:
  // cancelledRef was reset to false when a new session started. Any
  // earlier-session callback that fired AFTER the reset saw cancelledRef
  // = false and proceeded — the boolean couldn't distinguish "session
  // alive but new" from "this specific session is alive". A monotonic
  // counter resolves the ambiguity.
  const sessionIdRef = useRef(0);
  const isCurrent = useCallback((id) => id === sessionIdRef.current, []);

  // Success coordination. The reward is GRANTED (and /confirm credited)
  // roughly 15s into the ad, while the fullscreen GPT iframe still covers
  // the page — a snackbar fired then is never seen. So we hold the visible
  // success until BOTH the credit has landed (creditReadyRef) AND the ad
  // iframe has closed (adClosedRef). finishSuccess() fires once both are
  // true; finishedRef guards against a double-fire. All three reset at the
  // top of startAd().
  const creditReadyRef = useRef(false);
  const adClosedRef = useRef(false);
  const finishedRef = useRef(false);
  // Pet being watched-for this session, so the success confirmation can name
  // it ("Vote for Buddy now") after the ad closes. Set at startAd.
  const lastPetRef = useRef({ petId: null, petName: '' });

  // Ad-blocker check only — fires regardless of UI state.
  useEffect(() => {
    if (!enabled || !apiToken) return;
    if (userAdblock) {
      dispatch({ type: 'set', status: REWARDED_AD_STATE.BLOCKED });
      trackRewardedAdBlocked({});
    }
  }, [enabled, apiToken, userAdblock]);

  // Cap status: the /nonce response returns daily_remaining + daily_cap on
  // every successful issuance, so we don't need to poll. We prefetch once
  // as soon as the feature is enabled (see effect below) so the post-vote
  // modal surface can decide eligibility without an extra round-trip the
  // user can see.
  const refreshCap = useCallback(async () => {
    if (!enabled || !apiToken) return;
    try {
      const r = await getRewardedAdStatus(apiToken);
      const data = await r.json();
      dispatch({
        type: 'cap',
        remaining: data.daily_remaining,
        used: data.daily_used || 0,
        cap: typeof data.daily_cap === 'number' ? data.daily_cap : null,
      });
      if (data.daily_remaining <= 0) {
        dispatch({ type: 'set', status: REWARDED_AD_STATE.CAP_REACHED });
      }
    } catch (e) {
      // Soft-fail; the next /nonce attempt will surface real cap state.
    }
  }, [enabled, apiToken]);

  // Prefetch cap state as soon as the rewarded feature flips enabled. This
  // primes `dailyRemaining` before any post-vote moment so the invitation
  // modal doesn't have to fall back to the legacy share popup on first
  // attempt just because cap was unknown.
  useEffect(() => {
    if (enabled && apiToken) refreshCap();
  }, [enabled, apiToken, refreshCap]);

  // Aborts in-flight resources for the current session. Does NOT manipulate
  // sessionIdRef — bumping the id is the responsibility of whoever starts
  // the next session (or unmount).
  const cleanup = useCallback(() => {
    if (adLoadTimeoutRef.current) {
      clearTimeout(adLoadTimeoutRef.current);
      adLoadTimeoutRef.current = null;
    }
    if (nonceCtrlRef.current) {
      try { nonceCtrlRef.current.abort(); } catch (e) { /* ignore */ }
      nonceCtrlRef.current = null;
    }
    if (confirmCtrlRef.current) {
      try { confirmCtrlRef.current.abort(); } catch (e) { /* ignore */ }
      confirmCtrlRef.current = null;
    }
    if (slotRef.current) {
      slotRef.current.destroy();
      slotRef.current = null;
    }
  }, []);

  // Unmount: bump sessionIdRef to a value no captured closure will ever
  // match, then abort in-flight work. Any callback resolving after this
  // point sees a stale id and exits without dispatching to a torn-down
  // component.
  useEffect(() => () => {
    sessionIdRef.current += 1;
    cleanup();
  }, [cleanup]);

  // Fire the visible success exactly once, and only when the reward has
  // been credited AND the ad iframe has closed. Either order is safe:
  // grant→confirm usually resolves long before the user closes, but a user
  // who closes mid-confirm is covered because callConfirm calls this again
  // once the credit lands.
  const finishSuccess = useCallback((sessionId) => {
    if (!isCurrent(sessionId)) return;
    if (!creditReadyRef.current || !adClosedRef.current || finishedRef.current) return;
    finishedRef.current = true;
    dispatch({ type: 'set', status: REWARDED_AD_STATE.COMPLETED });
    if (typeof onSuccessVisible === 'function') onSuccessVisible(lastPetRef.current);
  }, [isCurrent, onSuccessVisible]);

  /**
   * The credit call. Frontend invokes this when GPT signals the user
   * earned the reward. Clears the cooldown silently (the ad is still on
   * screen); the visible success is deferred to finishSuccess() on close.
   */
  const callConfirm = useCallback(async ({
    nonce, petId, surface, sessionId,
  }) => {
    if (!isCurrent(sessionId)) return;
    dispatch({ type: 'set', status: REWARDED_AD_STATE.CONFIRMING });

    const transactionId = `web_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

    // Single attempt, with retry handled by the outer loop. Returns one of:
    //   { kind: 'ok', json }
    //   { kind: 'too_early' }      → retry
    //   { kind: 'failed', error }  → give up
    //   { kind: 'cap_reached' }    → give up
    //   { kind: 'invalid' }        → give up (nonce expired/consumed)
    const attemptConfirm = async () => {
      const ctrl = new AbortController();
      confirmCtrlRef.current = ctrl;
      const timer = setTimeout(() => ctrl.abort(), CONFIRM_TIMEOUT_MS);
      try {
        const captchaToken = await getCaptchaToken('rewarded_ad_confirm');
        const res = await confirmRewardedAd(apiToken, nonce, transactionId, captchaToken, ctrl.signal);
        clearTimeout(timer);

        if (res.status === 425) return { kind: 'too_early' };
        if (res.status === 410) return { kind: 'invalid' };
        if (res.status === 403) return { kind: 'cap_reached' };
        if (!res.ok) return { kind: 'failed', error: new Error(`confirm_${res.status}`) };
        const json = await res.json();
        return { kind: 'ok', json };
      } catch (e) {
        clearTimeout(timer);
        return { kind: 'failed', error: e };
      } finally {
        confirmCtrlRef.current = null;
      }
    };

    let lastTooEarly = false;
    for (let attempt = 0; attempt < CONFIRM_MAX_RETRIES; attempt += 1) {
      if (!isCurrent(sessionId)) return;
      // eslint-disable-next-line no-await-in-loop
      const r = await attemptConfirm();
      if (!isCurrent(sessionId)) return;

      if (r.kind === 'ok') {
        // Backend returns both `daily_cap` and `daily_remaining` so we can
        // compute used without baking the cap into the client. Falls back
        // to optimistic-increment if the response is missing fields (e.g.
        // an older backend deploy hasn't shipped the daily_cap yet).
        const cap = typeof r.json.daily_cap === 'number' ? r.json.daily_cap : null;
        const remaining = r.json.daily_remaining;
        let usedNext;
        if (cap !== null && typeof remaining === 'number') {
          usedNext = Math.max(0, cap - remaining);
        } else {
          usedNext = state.dailyUsed + 1;
        }
        dispatch({
          type: 'cap',
          remaining: typeof remaining === 'number' ? remaining : 0,
          used: usedNext,
          cap,
        });
        trackRewardedAdSsvConfirmed({ pet_id: petId, surface });
        // Silent: clears the cooldown in Redux/localStorage. The ad iframe
        // is typically still on screen here, so no visible feedback yet.
        if (typeof onCooldownCleared === 'function') {
          onCooldownCleared(r.json);
        }
        // Hand off to finishSuccess: it transitions to COMPLETED and fires
        // the snackbar, but only once the ad has actually closed.
        creditReadyRef.current = true;
        finishSuccess(sessionId);
        return;
      }
      if (r.kind === 'invalid') {
        dispatch({ type: 'set', status: REWARDED_AD_STATE.FAILED });
        trackRewardedAdSsvTimeout({ pet_id: petId, surface });
        return;
      }
      if (r.kind === 'cap_reached') {
        dispatch({ type: 'set', status: REWARDED_AD_STATE.CAP_REACHED });
        trackRewardedAdCapReached({ pet_id: petId, surface });
        return;
      }
      if (r.kind === 'failed') {
        captureException(r.error, { flow: 'rewarded_ad_confirm' });
        dispatch({ type: 'set', status: REWARDED_AD_STATE.FAILED });
        trackRewardedAdSsvTimeout({ pet_id: petId, surface });
        return;
      }
      // 425 Too Early — SSV credit pool is empty, Google's callback hasn't
      // landed yet. Wait and retry.
      lastTooEarly = true;
      if (attempt < CONFIRM_MAX_RETRIES - 1) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, CONFIRM_RETRY_DELAY_MS));
      }
    }

    if (lastTooEarly && isCurrent(sessionId)) {
      // Exhausted retries waiting for SSV. Surface as a timeout-style
      // failure — the user's nonce is still valid and they could try
      // again via the UI's "no_fill" retry, but in practice this is
      // either a Google outage or an attacker who never watched an ad.
      dispatch({ type: 'set', status: REWARDED_AD_STATE.FAILED });
      trackRewardedAdSsvTimeout({ pet_id: petId, surface });
    }
  }, [apiToken, onCooldownCleared, state.dailyUsed, isCurrent, finishSuccess]);

  const startAd = useCallback(async ({ petId, petName = '', surface = 'popup' } = {}) => {
    if (!enabled || !apiToken || !petId) return;
    if (![
      REWARDED_AD_STATE.IDLE,
      REWARDED_AD_STATE.FAILED,
      REWARDED_AD_STATE.NO_FILL,
    ].includes(state.status)) return;

    // Bump and capture the session id BEFORE any await. Every async
    // callback below captures this value in its closure and bails if a
    // newer session has started in the meantime. Tear down in-flight
    // resources from the previous session immediately afterwards.
    sessionIdRef.current += 1;
    const sessionId = sessionIdRef.current;
    // Reset the success-coordination flags for this fresh watch.
    creditReadyRef.current = false;
    adClosedRef.current = false;
    finishedRef.current = false;
    lastPetRef.current = { petId, petName };
    cleanup();

    trackRewardedAdCtaClicked({
      pet_id: petId,
      surface,
      daily_count_so_far: state.dailyUsed,
    });

    // 1. Request nonce
    dispatch({ type: 'set', status: REWARDED_AD_STATE.REQUESTING_NONCE });
    let nonceData;
    try {
      const captchaToken = await getCaptchaToken('rewarded_ad');
      if (!isCurrent(sessionId)) return;
      const ctrl = new AbortController();
      nonceCtrlRef.current = ctrl;
      const timer = setTimeout(() => ctrl.abort(), NONCE_TIMEOUT_MS);
      const res = await requestRewardedAdNonce(apiToken, petId, captchaToken, ctrl.signal);
      clearTimeout(timer);
      nonceCtrlRef.current = null;
      if (!isCurrent(sessionId)) return;

      if (res.status === 403 || res.status === 429) {
        dispatch({ type: 'set', status: REWARDED_AD_STATE.CAP_REACHED });
        trackRewardedAdCapReached({ pet_id: petId, surface });
        return;
      }
      if (!res.ok) throw new Error(`nonce_${res.status}`);
      nonceData = await res.json();
      if (!isCurrent(sessionId)) return;
      dispatch({
        type: 'cap',
        remaining: nonceData.daily_remaining,
        used: state.dailyUsed,
      });
      trackRewardedAdNonceRequested({
        pet_id: petId,
        surface,
        daily_count_so_far: state.dailyUsed,
      });
    } catch (e) {
      nonceCtrlRef.current = null;
      if (!isCurrent(sessionId)) return;
      captureException(e, { flow: 'rewarded_ad_nonce' });
      dispatch({ type: 'set', status: REWARDED_AD_STATE.FAILED });
      trackRewardedAdLoadFailed({ pet_id: petId, surface, reason: 'nonce_failed' });
      return;
    }

    // 2. Define rewarded slot. Auto-shows on rewardedSlotReady.
    dispatch({ type: 'set', status: REWARDED_AD_STATE.LOADING_AD });
    adLoadTimeoutRef.current = setTimeout(() => {
      adLoadTimeoutRef.current = null;
      if (!isCurrent(sessionId)) return;
      lastFillRef.current = { status: 'no_fill', at: Date.now() };
      dispatch({ type: 'set', status: REWARDED_AD_STATE.NO_FILL });
      trackRewardedAdLoadFailed({ pet_id: petId, surface, reason: 'timeout' });
      if (slotRef.current) {
        slotRef.current.destroy();
        slotRef.current = null;
      }
    }, AD_LOAD_TIMEOUT_MS);

    slotRef.current = registerRewardedSlot({
      // No customData on web GPT — see RewardedAdController::confirm doc.
      targeting: { pet_id: String(petId) },
      onLoaded: () => {
        if (!isCurrent(sessionId)) return;
        if (adLoadTimeoutRef.current) {
          clearTimeout(adLoadTimeoutRef.current);
          adLoadTimeoutRef.current = null;
        }
        lastFillRef.current = { status: 'fill', at: Date.now() };
        trackRewardedAdLoaded({ pet_id: petId, surface });
        dispatch({ type: 'set', status: REWARDED_AD_STATE.PLAYING });
      },
      onCompleted: () => {
        if (!isCurrent(sessionId)) return;
        trackRewardedAdCompleted({ pet_id: petId, surface });
        callConfirm({
          nonce: nonceData.nonce, petId, surface, sessionId,
        });
      },
      onClosed: ({ rewardGranted }) => {
        // Guard FIRST — a late close from a prior session must not destroy
        // the current session's slot. The session-id check decides whether
        // this event belongs to the slot in slotRef.current.
        if (!isCurrent(sessionId)) return;
        if (slotRef.current) {
          slotRef.current.destroy();
          slotRef.current = null;
        }
        if (rewardGranted) {
          // The user watched the ad and earned the reward. The credit is
          // handled by onCompleted→callConfirm; here we mark the ad closed
          // and let finishSuccess show the visible success now that the
          // fullscreen iframe is gone. If confirm is still in flight, the
          // callConfirm success branch will call finishSuccess again.
          adClosedRef.current = true;
          finishSuccess(sessionId);
          return;
        }
        dispatch({ type: 'set', status: REWARDED_AD_STATE.IDLE });
        trackRewardedAdDismissed({ pet_id: petId, surface });
      },
      onNoFill: () => {
        if (!isCurrent(sessionId)) return;
        if (adLoadTimeoutRef.current) {
          clearTimeout(adLoadTimeoutRef.current);
          adLoadTimeoutRef.current = null;
        }
        lastFillRef.current = { status: 'no_fill', at: Date.now() };
        dispatch({ type: 'set', status: REWARDED_AD_STATE.NO_FILL });
        trackRewardedAdLoadFailed({ pet_id: petId, surface, reason: 'no_fill' });
      },
    });

    if (!slotRef.current) {
      // GPT returned null — viewport doesn't support rewarded or GPT not loaded.
      if (!isCurrent(sessionId)) return;
      lastFillRef.current = { status: 'no_fill', at: Date.now() };
      dispatch({ type: 'set', status: REWARDED_AD_STATE.NO_FILL });
      trackRewardedAdLoadFailed({ pet_id: petId, surface, reason: 'gpt_unavailable' });
    }
  }, [enabled, apiToken, state.status, state.dailyUsed, cleanup, callConfirm, isCurrent, finishSuccess]);

  // True if the most recent ad attempt was a no_fill within the last
  // FILL_SUPPRESS_MS. Caller should suppress speculative invitations (e.g.
  // the post-vote modal) when this is true to avoid the "click → no ad"
  // disappointment loop.
  const hasRecentNoFill = useCallback(() => {
    const FILL_SUPPRESS_MS = 60_000;
    const { status, at } = lastFillRef.current;
    return status === 'no_fill' && (Date.now() - at) < FILL_SUPPRESS_MS;
  }, []);

  return {
    enabled,
    status: state.status,
    dailyRemaining: state.dailyRemaining,
    dailyUsed: state.dailyUsed,
    dailyCap: state.dailyCap,
    isAvailable: enabled
      && state.status !== REWARDED_AD_STATE.CAP_REACHED
      && state.status !== REWARDED_AD_STATE.BLOCKED
      && (state.dailyRemaining === null || state.dailyRemaining > 0),
    hasRecentNoFill,
    startAd,
    refreshCap,
  };
}
